import apiV2 from "api-v2";

export interface QuoteTemplateColumnPayload {
	column_type: string;
	label: string;
	sort_order: number;
}

export interface QuoteTemplateRowPayload {
	name: string;
	sort_order: number;
	unit_id: number | null;
}

export interface SaveQuoteTemplatePayload {
	name: string;
	columns: QuoteTemplateColumnPayload[];
	rows: QuoteTemplateRowPayload[];
}

export interface QuoteTemplateColumn {
	id: number;
	column_type: string;
	label: string;
	sort_order: number;
}

export interface QuoteTemplateRow {
	id: number;
	name: string;
	sort_order: number;
	unit_id: number | null;
	unit_name?: string;
}

export interface QuoteTemplate {
	id: number;
	name: string;
	columns: QuoteTemplateColumn[];
	created_at?: string;
	rows: QuoteTemplateRow[];
	updated_at?: string;
}

const TEMPLATES_PATH = "rfx-settings/quote-templates";

interface RawTemplateColumn {
	name: string;
	sort_order: number;
	type: string;
}

interface RawTemplateRow {
	name: string;
	sort_order: number;
	unit_id: number | null;
	unit_name?: string;
}

interface RawQuoteTemplate {
	id: number;
	name: string;
	created_at?: string;
	structure: {
		columns: RawTemplateColumn[];
		rows: RawTemplateRow[];
	};
	updated_at?: string;
}

const normalizeTemplate = (raw: RawQuoteTemplate): QuoteTemplate => ({
	id: raw.id,
	name: raw.name,
	columns: (raw.structure?.columns || []).map((c, idx) => ({
		id: idx,
		column_type: c.type,
		label: c.name,
		sort_order: c.sort_order,
	})),
	rows: (raw.structure?.rows || []).map((r, idx) => ({
		id: idx,
		name: r.name,
		unit_id: r.unit_id,
		unit_name: r.unit_name,
		sort_order: r.sort_order,
	})),
	created_at: raw.created_at,
	updated_at: raw.updated_at,
});

export const getQuoteTemplates = async (): Promise<QuoteTemplate[]> => {
	const response = await apiV2.get<{ data: RawQuoteTemplate[] }>(TEMPLATES_PATH);
	return response.data.data.map(normalizeTemplate);
};

export const getQuoteTemplate = async (
	templateId: number
): Promise<QuoteTemplate> => {
	const response = await apiV2.get<{ data: RawQuoteTemplate }>(
		`${TEMPLATES_PATH}/${templateId}`
	);
	return normalizeTemplate(response.data.data);
};

export const createQuoteTemplate = async (
	payload: SaveQuoteTemplatePayload
): Promise<QuoteTemplate> => {
	const response = await apiV2.post<{ data: QuoteTemplate }>(
		TEMPLATES_PATH,
		payload
	);
	return response.data.data;
};

export const updateQuoteTemplate = async (
	templateId: number,
	payload: SaveQuoteTemplatePayload
): Promise<QuoteTemplate> => {
	const response = await apiV2.patch<{ data: QuoteTemplate }>(
		`${TEMPLATES_PATH}/${templateId}`,
		payload
	);
	return response.data.data;
};

export const deleteQuoteTemplate = async (
	templateId: number
): Promise<void> => {
	await apiV2.delete(`${TEMPLATES_PATH}/${templateId}`);
};
